import React, { useEffect, useState } from "react";
import { api } from "../../lib/apiClient";
import { Database, Trash2, RefreshCw, AlertCircle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

const KIND_LABELS = {
  supplier_pdf: "Supplier PDFs (materials & access hire)",
  pdf:          "Supplier PDFs (legacy tag)",
  image:        "Image attachments",
  msg:          "Outlook .msg attachments",
  eml:          "Email .eml attachments",
  export:       "Quote Matrix exports (temporary)",
  login_logo:   "Login page logo",
  unknown:      "Other / untagged",
};

function fmtBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = Number(n);
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v < 10 ? v.toFixed(2) : v.toFixed(1)} ${units[i]}`;
}

export function StorageHealthTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/storage-health");
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load storage health");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const purge = async () => {
    setPurging(true);
    try {
      const { data: res } = await api.post("/admin/storage-health/purge");
      const lines = [];
      if (res.deleted_drafts) lines.push(`${res.deleted_drafts} orphaned draft${res.deleted_drafts === 1 ? "" : "s"}`);
      if (res.deleted_exports) lines.push(`${res.deleted_exports} expired export${res.deleted_exports === 1 ? "" : "s"}`);
      toast.success(`Purged ${lines.join(" + ") || "nothing"} — freed ${fmtBytes(res.freed_bytes)}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    } finally { setPurging(false); }
  };

  const purgeable = (data?.orphaned_drafts || 0) + (data?.expired_exports || 0);

  return (
    <div className="space-y-6" data-testid="storage-health-tab">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
            <Database size={20} className="text-brand-orange" />
            Storage Health
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-prose">
            All uploaded supplier PDFs, attachments and exported Quote Matrix PDFs live in MongoDB GridFS — see what&apos;s taking space and clean up safely.
          </p>
        </div>
        <Button onClick={load} variant="outline" disabled={loading} data-testid="refresh-storage-btn">
          <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Headline cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card label="Total files"      value={data?.total_files ?? "—"} testId="stat-total-files" />
        <Card label="Total storage"    value={fmtBytes(data?.total_bytes)} testId="stat-total-bytes" />
        <Card label="Orphaned drafts"  value={data?.orphaned_drafts ?? "—"}
              hint={`Uploads marked as draft & never linked to a quote, older than ${data?.orphan_threshold_days ?? 7} days.`}
              tone={data?.orphaned_drafts > 0 ? "warn" : "ok"} testId="stat-orphans" />
        <Card label="Expired exports"  value={data?.expired_exports ?? "—"}
              hint="Temporary export tokens past their 10-minute TTL — safe to delete." 
              tone={data?.expired_exports > 0 ? "warn" : "ok"} testId="stat-expired" />
      </div>

      {/* Per-kind breakdown */}
      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider font-bold text-slate-700">Breakdown by type</div>
          {data?.generated_at && (
            <div className="text-[11px] text-slate-500">
              Snapshot at {new Date(data.generated_at).toLocaleTimeString()}
            </div>
          )}
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white border-b border-slate-100 text-[11px] uppercase tracking-wider text-slate-500">
              <th className="text-left px-4 py-2">Kind</th>
              <th className="text-right px-4 py-2 w-24">Files</th>
              <th className="text-right px-4 py-2 w-32">Size</th>
              <th className="text-right px-4 py-2 w-32">% of total</th>
            </tr>
          </thead>
          <tbody data-testid="storage-breakdown-rows">
            {(data?.by_kind || []).length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500 italic">
                {loading ? "Loading…" : "No files stored yet."}
              </td></tr>
            )}
            {(data?.by_kind || []).map((row) => {
              const pct = data.total_bytes > 0 ? (row.bytes / data.total_bytes) * 100 : 0;
              return (
                <tr key={row.kind} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-slate-900">{KIND_LABELS[row.kind] || row.kind}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{row.kind}</div>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{row.count.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmtBytes(row.bytes)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{pct.toFixed(1)}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Purge action */}
      <div className="rounded-lg border border-amber-200 bg-amber-50/50 px-4 py-3 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900">Safe cleanup</div>
            <div className="text-xs text-slate-600 mt-0.5">
              Removes orphaned drafts older than {data?.orphan_threshold_days ?? 7} days and all expired export tokens. Never touches attachments linked to a quote.
            </div>
          </div>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="destructive"
              disabled={purgeable === 0 || purging}
              data-testid="purge-storage-btn"
              className="shrink-0"
            >
              <Trash2 size={14} className="mr-2" />
              {purging ? "Purging…" : purgeable === 0 ? "Nothing to clean" : `Purge ${purgeable} item${purgeable === 1 ? "" : "s"}`}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Purge orphaned files?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete {data?.orphaned_drafts || 0} orphaned draft{(data?.orphaned_drafts || 0) === 1 ? "" : "s"} and {data?.expired_exports || 0} expired export{(data?.expired_exports || 0) === 1 ? "" : "s"} from MongoDB GridFS. Files attached to a quote are never touched. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="purge-cancel-btn">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={purge} data-testid="purge-confirm-btn">
                Delete {purgeable} file{purgeable === 1 ? "" : "s"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function Card({ label, value, hint, tone = "neutral", testId }) {
  const toneCls = tone === "warn"
    ? "border-amber-200 bg-amber-50"
    : tone === "ok"
      ? "border-emerald-200 bg-emerald-50/40"
      : "border-slate-200 bg-white";
  return (
    <div className={`rounded-lg border ${toneCls} px-4 py-3`} data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="text-2xl font-display font-black tracking-tighter text-slate-900 mt-1 tabular-nums">{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1.5 leading-snug">{hint}</div>}
    </div>
  );
}
